import React, { useEffect, useState } from 'react';
import { todayIso } from '../utils/dateUtils';

function FoodReports({ token, selectedProperty }) {
  const detectMobileUi = () => {
    if (typeof window === 'undefined') return false;
    const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(window.navigator?.userAgent || '');
    return (coarsePointer || uaMobile) && window.innerWidth <= 1024;
  };
  const [isMobile, setIsMobile] = useState(() => detectMobileUi());
  const [kpi, setKpi] = useState(null);
  const [operational, setOperational] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [shrinkage, setShrinkage] = useState([]);
  const [dailyConsumptionRows, setDailyConsumptionRows] = useState([]);
  const [dailyDate, setDailyDate] = useState(todayIso());
  const [error, setError] = useState('');
  const [params, setParams] = useState({ from: '', to: '', food_revenue: '', opening_stock: '' });
  const [filters, setFilters] = useState({ daily: '', operational: '', financial: '', shrinkage: '' });
  const tapMin = { minHeight: '44px' };

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = () => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId });
  const parseResponse = async (res) => {
    const raw = await res.text();
    try { const json = raw ? JSON.parse(raw) : {}; return { data: json, message: json?.error || '' }; } catch { return { data: {}, message: raw || '' }; }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(detectMobileUi());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const loadOperational = async () => {
    try {
      const res = await fetch('/api/v1/food/reports/operational', { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load operational report');
      setOperational(data);
    } catch { setError('Network error while loading operational report'); }
  };
  const loadKpi = async () => {
    const q = new URLSearchParams();
    if (params.food_revenue) q.set('food_revenue', params.food_revenue);
    if (params.opening_stock) q.set('opening_stock', params.opening_stock);
    try {
      const res = await fetch(`/api/v1/food/reports/kpi?${q.toString()}`, { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load KPI dashboard');
      setKpi(data);
    } catch { setError('Network error while loading KPI dashboard'); }
  };
  const loadFinancial = async () => {
    const q = new URLSearchParams();
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.food_revenue) q.set('food_revenue', params.food_revenue);
    if (params.opening_stock) q.set('opening_stock', params.opening_stock);
    try {
      const res = await fetch(`/api/v1/food/reports/financial?${q.toString()}`, { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load financial report');
      setFinancial(data);
    } catch { setError('Network error while loading financial report'); }
  };
  const loadShrinkage = async () => {
    try {
      const res = await fetch('/api/v1/food/reports/shrinkage', { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load shrinkage report');
      setShrinkage(Array.isArray(data) ? data : []);
    } catch { setError('Network error while loading shrinkage report'); }
  };

  const loadDailyConsumption = async () => {
    const q = new URLSearchParams();
    if (dailyDate) q.set('date', dailyDate);
    try {
      const res = await fetch(`/api/v1/food/reports/daily-consumption?${q.toString()}`, { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load daily consumption report');
      setDailyConsumptionRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch {
      setError('Network error while loading daily consumption report');
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) return setError('Select a property first from Hotel Config');
    setError('');
    loadKpi();
    loadOperational();
    loadFinancial();
    loadShrinkage();
    loadDailyConsumption();
  }, [token, chainId, propertyId]);

  const box = { background: '#f5f6fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', overflowX: 'auto' };
  const input = { ...tapMin, padding: '0.5rem', border: '1px solid #dcdde1', borderRadius: '6px', fontSize: isMobile ? '16px' : '14px' };
  const btn = { ...tapMin, padding: '0.5rem 0.85rem', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', background: '#2f855a', fontSize: isMobile ? '15px' : '14px' };
  const exportBtn = { ...tapMin, padding: '0.45rem 0.75rem', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', background: '#1f6feb', fontSize: isMobile ? '15px' : '14px' };
  const exportExcelBtn = { ...exportBtn, background: '#6f42c1' };
  const kpiCard = { background: '#fff', border: '1px solid #dcdde1', borderRadius: '8px', padding: '0.8rem' };
  const tableStyle = { width: '100%', minWidth: '760px', borderCollapse: 'collapse', background: '#fff' };
  const thStyle = { padding: '0.5rem', textAlign: 'left', background: '#2f855a', color: '#fff' };
  const tdStyle = { padding: '0.5rem' };
  const trend = Array.isArray(kpi?.monthly_consumption_trend) ? kpi.monthly_consumption_trend : [];
  const maxTrend = trend.reduce((m, t) => Math.max(m, Number(t.consumption_value || 0)), 0) || 1;
  const normalize = (v) => String(v ?? '').toLowerCase();
  const rowMatches = (row, keyword, keys) => {
    const q = normalize(keyword).trim();
    if (!q) return true;
    return keys.some((k) => normalize(row?.[k]).includes(q));
  };

  const escapeCsv = (value) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };

  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportRows = (filePrefix, rows, columns, type) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      setError('No data available to export');
      return;
    }
    const safeDate = new Date().toISOString().slice(0, 10);
    if (type === 'csv') {
      const header = columns.map((c) => escapeCsv(c.label)).join(',');
      const body = rows.map((row) => columns.map((c) => escapeCsv(row[c.key])).join(',')).join('\n');
      downloadFile(`${header}\n${body}`, `${filePrefix}_${safeDate}.csv`, 'text/csv;charset=utf-8;');
      return;
    }
    const thead = `<tr>${columns.map((c) => `<th>${String(c.label)}</th>`).join('')}</tr>`;
    const tbody = rows.map((row) => `<tr>${columns.map((c) => `<td>${String(row[c.key] ?? '')}</td>`).join('')}</tr>`).join('');
    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="UTF-8"></head>
      <body><table border="1"><thead>${thead}</thead><tbody>${tbody}</tbody></table></body>
      </html>`;
    downloadFile(html, `${filePrefix}_${safeDate}.xls`, 'application/vnd.ms-excel;charset=utf-8;');
  };

  const buildOperationalExportRows = () => {
    if (!operational) return [];
    const out = [];
    (operational.stock_on_hand || []).forEach((r) => out.push({
      report_type: 'Stock On Hand',
      store: r.store_name || '',
      item_code: r.item_code || '',
      item_name: r.item_name || '',
      qty_or_value_1: Number(r.on_hand_qty || 0).toFixed(3),
      qty_or_value_2: Number(r.stock_value || 0).toFixed(2),
      extra_1: '',
      extra_2: ''
    }));
    (operational.reorder_report || []).forEach((r) => out.push({
      report_type: 'Reorder',
      store: r.store_name || '',
      item_code: r.item_code || '',
      item_name: r.item_name || '',
      qty_or_value_1: Number(r.available_qty || 0).toFixed(3),
      qty_or_value_2: Number(r.reorder_level || 0).toFixed(3),
      extra_1: '',
      extra_2: ''
    }));
    (operational.expiry_report || []).forEach((r) => out.push({
      report_type: 'Expiry',
      store: r.store_name || '',
      item_code: r.item_code || '',
      item_name: r.item_name || '',
      qty_or_value_1: Number(r.remaining_qty || 0).toFixed(3),
      qty_or_value_2: '',
      extra_1: r.batch_no || '',
      extra_2: r.expiry_date ? String(r.expiry_date).slice(0, 10) : ''
    }));
    (operational.fast_moving_items || []).forEach((r) => out.push({
      report_type: 'Fast Moving',
      store: '',
      item_code: r.item_code || '',
      item_name: r.item_name || '',
      qty_or_value_1: Number(r.issued_qty_30d || 0).toFixed(3),
      qty_or_value_2: '',
      extra_1: '',
      extra_2: ''
    }));
    (operational.slow_moving_items || []).forEach((r) => out.push({
      report_type: 'Slow Moving',
      store: '',
      item_code: r.item_code || '',
      item_name: r.item_name || '',
      qty_or_value_1: Number(r.issued_qty_30d || 0).toFixed(3),
      qty_or_value_2: '',
      extra_1: '',
      extra_2: ''
    }));
    return out;
  };

  const buildFinancialExportRows = () => {
    if (!financial) return [];
    const out = [
      { report_type: 'Summary', key: 'Opening Stock', value_1: Number(financial.opening_stock || 0).toFixed(2), value_2: '' },
      { report_type: 'Summary', key: 'Purchases', value_1: Number(financial.purchases || 0).toFixed(2), value_2: '' },
      { report_type: 'Summary', key: 'Closing Stock', value_1: Number(financial.closing_stock || 0).toFixed(2), value_2: '' },
      { report_type: 'Summary', key: 'Consumption', value_1: Number(financial.consumption || 0).toFixed(2), value_2: '' },
      { report_type: 'Summary', key: 'Food Cost %', value_1: Number(financial.food_cost_percent || 0).toFixed(2), value_2: '%' }
    ];
    (financial.supplier_purchase_summary || []).forEach((r) => out.push({
      report_type: 'Supplier Purchase',
      key: r.company_name || '',
      value_1: Number(r.po_count || 0),
      value_2: Number(r.total_purchase || 0).toFixed(2)
    }));
    (financial.item_wise_cost_analysis || []).forEach((r) => out.push({
      report_type: 'Item Cost Analysis',
      key: `${r.item_code || ''} ${r.item_name || ''}`.trim(),
      value_1: Number(r.purchased_qty || 0).toFixed(3),
      value_2: Number(r.purchased_value || 0).toFixed(2)
    }));
    (financial.monthly_usage_trend || []).forEach((r) => out.push({
      report_type: 'Monthly Usage',
      key: r.month ? String(r.month).slice(0, 7) : '',
      value_1: Number(r.consumption_value || 0).toFixed(2),
      value_2: Number(r.purchase_value || 0).toFixed(2)
    }));
    return out;
  };

  const dailyRowsView = dailyConsumptionRows.filter((r) => rowMatches(r, filters.daily, ['item_code', 'item_name', 'unit']));
  const opStockOnHandView = (operational?.stock_on_hand || []).filter((r) => rowMatches(r, filters.operational, ['store_name', 'item_code', 'item_name']));
  const opReorderView = (operational?.reorder_report || []).filter((r) => rowMatches(r, filters.operational, ['store_name', 'item_code', 'item_name']));
  const opExpiryView = (operational?.expiry_report || []).filter((r) => rowMatches(r, filters.operational, ['store_name', 'item_code', 'item_name', 'batch_no']));
  const opFastView = (operational?.fast_moving_items || []).filter((r) => rowMatches(r, filters.operational, ['item_code', 'item_name']));
  const opSlowView = (operational?.slow_moving_items || []).filter((r) => rowMatches(r, filters.operational, ['item_code', 'item_name']));
  const finSupplierView = (financial?.supplier_purchase_summary || []).filter((r) => rowMatches(r, filters.financial, ['company_name']));
  const finItemCostView = (financial?.item_wise_cost_analysis || []).filter((r) => rowMatches(r, filters.financial, ['item_code', 'item_name']));
  const finMonthlyView = (financial?.monthly_usage_trend || []).filter((r) => rowMatches(r, filters.financial, ['month']));
  const shrinkageView = shrinkage.filter((r) => rowMatches(r, filters.shrinkage, ['item_code', 'item_name']));

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      <h2 style={{ color: '#2f855a' }}>Food Management - Reports</h2>
      {error && <div className="error-banner">{error}</div>}

      <div style={box}>
        <h3>KPI Dashboard</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '0.6rem', marginBottom: '0.8rem' }}>
          <div style={kpiCard}><div>Total Stock Value</div><div style={{ fontWeight: 'bold' }}>{Number(kpi?.total_stock_value || 0).toFixed(2)}</div></div>
          <div style={kpiCard}><div>Today's Issues</div><div style={{ fontWeight: 'bold' }}>{Number(kpi?.todays_issues || 0).toFixed(2)}</div></div>
          <div style={kpiCard}><div>Today's Purchases</div><div style={{ fontWeight: 'bold' }}>{Number(kpi?.todays_purchases || 0).toFixed(2)}</div></div>
          <div style={kpiCard}><div>Low Stock Count</div><div style={{ fontWeight: 'bold' }}>{Number(kpi?.low_stock_count || 0)}</div></div>
          <div style={kpiCard}><div>Expiring Items Count</div><div style={{ fontWeight: 'bold' }}>{Number(kpi?.expiring_items_count || 0)}</div></div>
          <div style={kpiCard}><div>Food Cost %</div><div style={{ fontWeight: 'bold' }}>{Number(kpi?.food_cost_percent || 0).toFixed(2)}%</div></div>
        </div>
        <div style={{ marginTop: '0.4rem' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.4rem' }}>Monthly Consumption Trend</div>
          {trend.length === 0 ? <div>No trend data</div> : (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${trend.length}, minmax(28px,1fr))`, gap: '0.35rem', alignItems: 'end', height: '160px', background: '#fff', border: '1px solid #dcdde1', borderRadius: '8px', padding: '0.6rem' }}>
              {trend.map((row, idx) => {
                const value = Number(row.consumption_value || 0);
                const h = Math.max(6, Math.round((value / maxTrend) * 110));
                const month = String(row.month || '').slice(0, 7);
                return (
                  <div key={`${month}-${idx}`} title={`${month}: ${value.toFixed(2)}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                    <div style={{ width: '100%', maxWidth: '26px', height: `${h}px`, background: '#2f855a', borderRadius: '4px 4px 0 0' }} />
                    <div style={{ fontSize: '0.7rem', color: '#4a5568', transform: 'rotate(-40deg)', transformOrigin: 'left top', whiteSpace: 'nowrap' }}>{month}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={box}>
        <h3>Financial Filters</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.6rem' }}>
          <input type="date" value={params.from} onChange={(e) => setParams({ ...params, from: e.target.value })} style={input} />
          <input type="date" value={params.to} onChange={(e) => setParams({ ...params, to: e.target.value })} style={input} />
          <input type="number" step="0.01" value={params.food_revenue} onChange={(e) => setParams({ ...params, food_revenue: e.target.value })} style={input} placeholder="Food Revenue" />
          <input type="number" step="0.01" value={params.opening_stock} onChange={(e) => setParams({ ...params, opening_stock: e.target.value })} style={input} placeholder="Opening Stock" />
        </div>
        <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={loadKpi} style={btn}>Refresh KPI</button>
          <button onClick={loadFinancial} style={btn}>Refresh Financial</button>
        </div>
      </div>

      <div style={box}>
        <h3>Daily Consumption Report (Item-wise)</h3>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          <input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} style={input} />
          <input type="text" value={filters.daily} onChange={(e) => setFilters({ ...filters, daily: e.target.value })} style={input} placeholder="Filter grid (item code/name/unit)" />
          <button onClick={loadDailyConsumption} style={btn}>Load Daily Report</button>
          <button
            onClick={() => exportRows('food_daily_consumption', dailyConsumptionRows, [
              { key: 'item_code', label: 'Item Code' },
              { key: 'item_name', label: 'Item Name' },
              { key: 'unit', label: 'Unit' },
              { key: 'opening_stock', label: 'Opening Stock' },
              { key: 'purchases', label: 'Purchases' },
              { key: 'closing_stock', label: 'Closing Stock' },
              { key: 'consumption', label: 'Consumption' }
            ], 'csv')}
            style={exportBtn}
          >
            Export CSV
          </button>
          <button
            onClick={() => exportRows('food_daily_consumption', dailyConsumptionRows, [
              { key: 'item_code', label: 'Item Code' },
              { key: 'item_name', label: 'Item Name' },
              { key: 'unit', label: 'Unit' },
              { key: 'opening_stock', label: 'Opening Stock' },
              { key: 'purchases', label: 'Purchases' },
              { key: 'closing_stock', label: 'Closing Stock' },
              { key: 'consumption', label: 'Consumption' }
            ], 'excel')}
            style={exportExcelBtn}
          >
            Export Excel
          </button>
        </div>
        {dailyRowsView.length === 0 ? <p>No daily consumption data</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Item</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Unit</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Opening Stock</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Purchases</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Closing Stock</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Consumption (Auto)</th>
              </tr>
            </thead>
            <tbody>
              {dailyRowsView.map((r, idx) => (
                <tr key={`${r.item_id || idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.5rem' }}>{`${r.item_code || ''} ${r.item_name || ''}`.trim()}</td>
                  <td style={{ padding: '0.5rem' }}>{r.unit || '-'}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.opening_stock || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.purchases || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.closing_stock || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem', fontWeight: 'bold' }}>{Number(r.consumption || 0).toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={box}>
        <h3>Operational Reports</h3>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          <input type="text" value={filters.operational} onChange={(e) => setFilters({ ...filters, operational: e.target.value })} style={input} placeholder="Filter operational grids" />
          <button
            onClick={() => exportRows('food_operational_reports', buildOperationalExportRows(), [
              { key: 'report_type', label: 'Report Type' },
              { key: 'store', label: 'Store' },
              { key: 'item_code', label: 'Item Code' },
              { key: 'item_name', label: 'Item Name' },
              { key: 'qty_or_value_1', label: 'Value 1' },
              { key: 'qty_or_value_2', label: 'Value 2' },
              { key: 'extra_1', label: 'Extra 1' },
              { key: 'extra_2', label: 'Extra 2' }
            ], 'csv')}
            style={exportBtn}
          >
            Export CSV
          </button>
          <button
            onClick={() => exportRows('food_operational_reports', buildOperationalExportRows(), [
              { key: 'report_type', label: 'Report Type' },
              { key: 'store', label: 'Store' },
              { key: 'item_code', label: 'Item Code' },
              { key: 'item_name', label: 'Item Name' },
              { key: 'qty_or_value_1', label: 'Value 1' },
              { key: 'qty_or_value_2', label: 'Value 2' },
              { key: 'extra_1', label: 'Extra 1' },
              { key: 'extra_2', label: 'Extra 2' }
            ], 'excel')}
            style={exportExcelBtn}
          >
            Export Excel
          </button>
        </div>
        {operational ? (
          <div>
            <div>{`Stock Valuation Total: ${Number(operational.stock_valuation_total || 0).toFixed(2)}`}</div>
            <div>{`Reorder Items: ${(operational.reorder_report || []).length}`}</div>
            <div>{`Expiry Items (30 days): ${(operational.expiry_report || []).length}`}</div>
            <div>{`Fast Moving Count: ${(operational.fast_moving_items || []).length}`}</div>
            <div>{`Slow Moving Count: ${(operational.slow_moving_items || []).length}`}</div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Stock on Hand / Stock Valuation</h4>
              {opStockOnHandView.length === 0 ? <p>No stock on hand data</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Store</th><th style={thStyle}>Item Code</th><th style={thStyle}>Item Name</th>
                    <th style={thStyle}>On Hand Qty</th><th style={thStyle}>Stock Value</th>
                  </tr></thead>
                  <tbody>{opStockOnHandView.map((r, idx) => (
                    <tr key={`soh-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.store_name || '-'}</td>
                      <td style={tdStyle}>{r.item_code || '-'}</td>
                      <td style={tdStyle}>{r.item_name || '-'}</td>
                      <td style={tdStyle}>{Number(r.on_hand_qty || 0).toFixed(3)}</td>
                      <td style={tdStyle}>{Number(r.stock_value || 0).toFixed(2)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Reorder Report</h4>
              {opReorderView.length === 0 ? <p>No reorder alerts</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Store</th><th style={thStyle}>Item Code</th><th style={thStyle}>Item Name</th>
                    <th style={thStyle}>Available Qty</th><th style={thStyle}>Reorder Level</th>
                  </tr></thead>
                  <tbody>{opReorderView.map((r, idx) => (
                    <tr key={`reo-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.store_name || '-'}</td>
                      <td style={tdStyle}>{r.item_code || '-'}</td>
                      <td style={tdStyle}>{r.item_name || '-'}</td>
                      <td style={tdStyle}>{Number(r.available_qty || 0).toFixed(3)}</td>
                      <td style={tdStyle}>{Number(r.reorder_level || 0).toFixed(3)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Expiry Report</h4>
              {opExpiryView.length === 0 ? <p>No expiry alerts</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Store</th><th style={thStyle}>Item Code</th><th style={thStyle}>Item Name</th>
                    <th style={thStyle}>Batch</th><th style={thStyle}>Expiry Date</th><th style={thStyle}>Remaining Qty</th>
                  </tr></thead>
                  <tbody>{opExpiryView.map((r, idx) => (
                    <tr key={`exp-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.store_name || '-'}</td>
                      <td style={tdStyle}>{r.item_code || '-'}</td>
                      <td style={tdStyle}>{r.item_name || '-'}</td>
                      <td style={tdStyle}>{r.batch_no || '-'}</td>
                      <td style={tdStyle}>{r.expiry_date ? String(r.expiry_date).slice(0, 10) : '-'}</td>
                      <td style={tdStyle}>{Number(r.remaining_qty || 0).toFixed(3)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Fast-moving Items</h4>
              {opFastView.length === 0 ? <p>No fast-moving data</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Item Code</th><th style={thStyle}>Item Name</th><th style={thStyle}>Issued Qty (30d)</th>
                  </tr></thead>
                  <tbody>{opFastView.map((r, idx) => (
                    <tr key={`fm-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.item_code || '-'}</td>
                      <td style={tdStyle}>{r.item_name || '-'}</td>
                      <td style={tdStyle}>{Number(r.issued_qty_30d || 0).toFixed(3)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Slow-moving Items</h4>
              {opSlowView.length === 0 ? <p>No slow-moving data</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Item Code</th><th style={thStyle}>Item Name</th><th style={thStyle}>Issued Qty (30d)</th>
                  </tr></thead>
                  <tbody>{opSlowView.map((r, idx) => (
                    <tr key={`sm-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.item_code || '-'}</td>
                      <td style={tdStyle}>{r.item_name || '-'}</td>
                      <td style={tdStyle}>{Number(r.issued_qty_30d || 0).toFixed(3)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        ) : <p>No operational data</p>}
      </div>

      <div style={box}>
        <h3>Financial Reports</h3>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          <input type="text" value={filters.financial} onChange={(e) => setFilters({ ...filters, financial: e.target.value })} style={input} placeholder="Filter financial grids" />
          <button
            onClick={() => exportRows('food_financial_reports', buildFinancialExportRows(), [
              { key: 'report_type', label: 'Report Type' },
              { key: 'key', label: 'Key' },
              { key: 'value_1', label: 'Value 1' },
              { key: 'value_2', label: 'Value 2' }
            ], 'csv')}
            style={exportBtn}
          >
            Export CSV
          </button>
          <button
            onClick={() => exportRows('food_financial_reports', buildFinancialExportRows(), [
              { key: 'report_type', label: 'Report Type' },
              { key: 'key', label: 'Key' },
              { key: 'value_1', label: 'Value 1' },
              { key: 'value_2', label: 'Value 2' }
            ], 'excel')}
            style={exportExcelBtn}
          >
            Export Excel
          </button>
        </div>
        {financial ? (
          <div>
            <div>{`Opening Stock: ${Number(financial.opening_stock || 0).toFixed(2)}`}</div>
            <div>{`Purchases: ${Number(financial.purchases || 0).toFixed(2)}`}</div>
            <div>{`Closing Stock: ${Number(financial.closing_stock || 0).toFixed(2)}`}</div>
            <div>{`Consumption: ${Number(financial.consumption || 0).toFixed(2)}`}</div>
            <div>{`Food Revenue: ${Number(financial.food_revenue || 0).toFixed(2)}`}</div>
            <div style={{ fontWeight: 'bold' }}>{`Food Cost %: ${Number(financial.food_cost_percent || 0).toFixed(2)}%`}</div>
            <div>{`Consumption Report (Direct): ${Number(financial?.consumption_report?.direct_consumption_value || 0).toFixed(2)}`}</div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Supplier Purchase Summary</h4>
              {finSupplierView.length === 0 ? <p>No supplier purchase summary</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Supplier</th><th style={thStyle}>PO Count</th><th style={thStyle}>Total Purchase</th>
                  </tr></thead>
                  <tbody>{finSupplierView.map((r, idx) => (
                    <tr key={`sup-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.company_name || '-'}</td>
                      <td style={tdStyle}>{Number(r.po_count || 0)}</td>
                      <td style={tdStyle}>{Number(r.total_purchase || 0).toFixed(2)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Item-wise Cost Analysis</h4>
              {finItemCostView.length === 0 ? <p>No item-wise cost analysis</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Item Code</th><th style={thStyle}>Item Name</th><th style={thStyle}>Purchased Qty</th>
                    <th style={thStyle}>Purchased Value</th><th style={thStyle}>Avg Cost</th>
                  </tr></thead>
                  <tbody>{finItemCostView.map((r, idx) => (
                    <tr key={`itm-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.item_code || '-'}</td>
                      <td style={tdStyle}>{r.item_name || '-'}</td>
                      <td style={tdStyle}>{Number(r.purchased_qty || 0).toFixed(3)}</td>
                      <td style={tdStyle}>{Number(r.purchased_value || 0).toFixed(2)}</td>
                      <td style={tdStyle}>{Number(r.avg_cost || 0).toFixed(4)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ margin: '0.4rem 0' }}>Monthly Usage Trend</h4>
              {finMonthlyView.length === 0 ? <p>No monthly usage trend</p> : (
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={thStyle}>Month</th><th style={thStyle}>Consumption Value</th><th style={thStyle}>Purchase Value</th>
                  </tr></thead>
                  <tbody>{finMonthlyView.map((r, idx) => (
                    <tr key={`mth-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td style={tdStyle}>{r.month ? String(r.month).slice(0, 7) : '-'}</td>
                      <td style={tdStyle}>{Number(r.consumption_value || 0).toFixed(2)}</td>
                      <td style={tdStyle}>{Number(r.purchase_value || 0).toFixed(2)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        ) : <p>No financial data</p>}
      </div>

      <div style={box}>
        <h3>Shrinkage Report</h3>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
          <input type="text" value={filters.shrinkage} onChange={(e) => setFilters({ ...filters, shrinkage: e.target.value })} style={input} placeholder="Filter shrinkage grid" />
          <button
            onClick={() => exportRows('food_shrinkage_report', shrinkage, [
              { key: 'item_code', label: 'Item Code' },
              { key: 'item_name', label: 'Item Name' },
              { key: 'shrinkage_qty', label: 'Shrinkage Qty' },
              { key: 'shrinkage_value', label: 'Shrinkage Value' }
            ], 'csv')}
            style={exportBtn}
          >
            Export CSV
          </button>
          <button
            onClick={() => exportRows('food_shrinkage_report', shrinkage, [
              { key: 'item_code', label: 'Item Code' },
              { key: 'item_name', label: 'Item Name' },
              { key: 'shrinkage_qty', label: 'Shrinkage Qty' },
              { key: 'shrinkage_value', label: 'Shrinkage Value' }
            ], 'excel')}
            style={exportExcelBtn}
          >
            Export Excel
          </button>
        </div>
        {shrinkageView.length === 0 ? <p>No shrinkage data</p> : (
          <table style={tableStyle}>
            <thead><tr>
              <th style={thStyle}>Item</th><th style={thStyle}>Shrinkage Qty</th><th style={thStyle}>Shrinkage Value</th>
            </tr></thead>
            <tbody>{shrinkageView.map((r, idx) => <tr key={idx} style={{ borderBottom: '1px solid var(--theme-border)' }}>
              <td style={tdStyle}>{`${r.item_code || ''} ${r.item_name || ''}`}</td><td style={tdStyle}>{Number(r.shrinkage_qty || 0).toFixed(3)}</td><td style={tdStyle}>{Number(r.shrinkage_value || 0).toFixed(2)}</td>
            </tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodReports;
